import { action } from '@reatom/framework'

import { pipe, Result } from '@blade/std-lib'
import * as DumpTokenInstruction from '@root/entities/dump-token-instruction'
import { Network } from '@root/entities/network'
import { TradeSettings } from '@root/entities/trade-settings'
import { TxHash, TxSerialized } from '@root/entities/transaction'
import { WalletId } from '@root/entities/wallet'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { ApiError, apiRequest } from '@root/shared/api'

import type { TypeHelper } from '@blade/std-lib'
import type {
  TradePrepareDumpTokensInstructionsCreatePayload,
  TradePreparePermitInstructionsCreatePayload as TradePreparePermitInstructionsCreatePayloadDto,
  TradePrepareSwapInstructionsCreatePayload,
  TradeSendMultipleSwapTransactionsCreateData,
  TradeSendMultipleSwapTransactionsCreatePayload,
  TradeSendSwapTransactionCreateData,
  TradeSendSwapTransactionCreatePayload,
} from '@blade/trade-api-client'
import type { CurrencyAddress } from '@root/entities/currency'
import type { Pair, PairAddress } from '@root/entities/pair'
import type { Provider } from '@root/entities/provider'
import type { Quote } from '@root/entities/quote'
import type { TxSigned } from '@root/entities/transaction'
import type { WalletAddress } from '@root/entities/wallet'
import type BigNumber from 'bignumber.js'

export interface ProcessSwapCommonArgs {
  network: Network.Network
  inAddress: CurrencyAddress
  outAddress: CurrencyAddress
  provider: Provider.Provider
  pairAddress: PairAddress
  /**
   * Non native currency of the pair
   */
  pairQuoteCurrency: CurrencyAddress
  pairQuoteTokenProgram: string
  swapSettings: TradeSettings.TradeSettings
  type: 'buy' | 'sell'
  accounts: Pair.Accounts | undefined
}

export interface ProcessSwapArgs extends ProcessSwapCommonArgs {
  id: string
  quote: Quote.Quote
  walletId: WalletId
  walletAddress: WalletAddress
  amount: BigNumber
  rawAmountIn?: string
}

export interface ProcessBulkSwapEntryArgs {
  walletId: WalletId
  walletAddress: WalletAddress
  amountIn: BigNumber
  amountOut: BigNumber
  rawAmountIn?: string
}

export interface ProcessBulkSwapArgs extends ProcessSwapCommonArgs {
  id: string
  swaps: Array<ProcessBulkSwapEntryArgs>
}

const mapRequestToDto = (
  req: ProcessSwapArgs,
): TradePrepareSwapInstructionsCreatePayload => {
  return {
    amount: req.amount.toString(),
    amountOut: req.quote.outputAmount.toNumber(),
    network: Network.toDto(req.network),
    pairAddress: req.pairAddress,
    path: [req.inAddress, req.outAddress],
    provider: req.provider.id,
    swapSettings: TradeSettings.toDto(req.swapSettings),
    walletId: req.walletId,
    tokenAddress: req.pairQuoteCurrency,
    walletAddress: req.walletAddress,
    type: req.type,
  }
}

interface TradePreparePermitInstructionsCreatePayload
  extends TypeHelper.OmitKey<
    TradePreparePermitInstructionsCreatePayloadDto,
    'walletId' | 'walletAddress' | 'tokenAddress'
  > {
  walletId: WalletId
  walletAddress: WalletAddress
  tokenAddress: CurrencyAddress
}

export const preparePermitInstructionsRequestAction = action(
  async (ctx, req: TradePreparePermitInstructionsCreatePayload) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)
    const result = await ctx.schedule(() =>
      apiRequest(
        ctx,
        tradeApiClient.trade.tradePreparePermitInstructionsCreate,
        [
          {
            ...req,
          },
        ],
      ),
    )
    return pipe(result, Result.mapError(ApiError.fallbackToUnknown))
  },
  'preparePermitInstructionsRequestAction',
)

export const prepareSwapInstructionsRequestAction = action(
  async (
    ctx,
    req: ProcessSwapArgs &
      Pick<TradePrepareSwapInstructionsCreatePayload, 'permit'>,
  ) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)
    const reqDto = mapRequestToDto(req)
    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.trade.tradePrepareSwapInstructionsCreate, [
        {
          ...reqDto,
          permit: req.permit,
        },
      ]),
    )
    return pipe(
      result,
      Result.map((value) => ({
        ...value,
        serializedTx: TxSerialized(value.serializedTx),
        feeSource:
          (value as { feeSource?: 'input' | 'output' }).feeSource ??
          req.quote.fee?.source ??
          (req.type === 'sell' ? 'output' : 'input'),
      })),
      Result.mapError(ApiError.fallbackToUnknown),
    )
  },
  'prepareSwapInstructionsRequestAction',
)

export const prepareDumpTokenInstructionsRequestAction = action(
  async (ctx, req: TradePrepareDumpTokensInstructionsCreatePayload) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)
    const result = await ctx.schedule(() =>
      apiRequest(
        ctx,
        tradeApiClient.trade.tradePrepareDumpTokensInstructionsCreate,
        [req],
      ),
    )
    return pipe(
      result,
      Result.map(DumpTokenInstruction.fromDtos),
      Result.mapError(ApiError.fallbackToUnknown),
    )
  },
  'prepareDumpTokenInstructionsRequestAction',
)

export type MultipleSwapTransaction = TypeHelper.OverrideKeys<
  TradeSendMultipleSwapTransactionsCreateData['txs'][number],
  {
    tx: TxHash
    walletId: WalletId
  }
>

export type MultipleSwapsResult = TypeHelper.OverrideKeys<
  TradeSendMultipleSwapTransactionsCreateData,
  {
    txs: Array<MultipleSwapTransaction>
  }
>

export const sendMultipleSwapTransactionsRequestAction = action(
  async (ctx, req: TradeSendMultipleSwapTransactionsCreatePayload) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)
    const result = await ctx.schedule(() =>
      apiRequest(
        ctx,
        tradeApiClient.trade.tradeSendMultipleSwapTransactionsCreate,
        [req],
      ),
    )
    return pipe(
      result,
      Result.map(
        (it): MultipleSwapsResult => ({
          ...it,
          txs: it.txs.map((tx) => ({
            ...tx,
            tx: TxHash(tx.tx),
            walletId: WalletId(tx.walletId),
          })),
        }),
      ),
      Result.mapError(ApiError.fallbackToUnknown),
    )
  },
  'sendMultipleSwapTransactionsRequestAction',
)

interface SendSwapTransactionRequestAction
  extends ProcessSwapArgs,
    Pick<
      TradeSendSwapTransactionCreatePayload,
      'permit' | 'swapOrderId' | 'txSignature'
    > {
  signedTx?: TxSigned
}

export type SendSwapData = TypeHelper.OverrideKeys<
  TradeSendSwapTransactionCreateData,
  {
    tx: TxHash
  }
>
export const sendSwapTransactionRequestAction = action(
  async (ctx, req: SendSwapTransactionRequestAction) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)
    const reqDto = mapRequestToDto(req)
    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.trade.tradeSendSwapTransactionCreate, [
        {
          ...reqDto,
          signedTx: req.signedTx,
          swapOrderId: req.swapOrderId,
          permit: req.permit,
          txSignature: req.txSignature,
        },
      ]),
    )
    return pipe(
      result,
      Result.map((x): SendSwapData => ({ ...x, tx: TxHash(x.tx) })),
      Result.mapError(ApiError.fallbackToUnknown),
    )
  },
  'sendSwapTransactionRequestAction',
)
